import { Tooltip } from "@src/lib/ui/LegacyTooltip";
import { Button } from "@src/lib/ui/Button";
import ArrowsRefreshIcon from "@src/lib/icons/18px/arrows-refresh";
import XIcon from "@src/lib/icons/18px/x";
import LanguageSelectorDropdown from "@src/features/contentDetail/ui/LanguageSelectorDropdown";
import Text from "@src/lib/ui/Text";
import { AnimatePresence, motion } from "framer-motion";
import { FC } from "react";

type Props = {
  selectedLanguage: Language;
  translationModeEnabled: boolean;
  onLanguageChange: (language: Language) => void;
  onClose?: () => void;
  retranslate?: (lang: Language) => void;
  retranslateLoading?: boolean;
};

const TranslationBanner: FC<Props> = ({
  selectedLanguage,
  translationModeEnabled,
  onLanguageChange,
  onClose,
  retranslate,
  retranslateLoading,
}) => {
  return (
    <AnimatePresence initial={false}>
      <motion.div
        className="relative flex items-center justify-evenly self-stretch overflow-hidden bg-blue-9"
        animate={
          translationModeEnabled
            ? "translationModeEnabled"
            : "translationModeDisabled"
        }
        variants={{
          translationModeEnabled: {
            height: 48,
            opacity: 1,
            transition: {
              delay: 0,
              duration: 0.3,
            },
          },
          translationModeDisabled: {
            height: 0,
            opacity: 0,
            transition: {
              delay: 0,
              duration: 0.3,
            },
          },
        }}
      >
        <div className="flex flex-1 items-center justify-center">
          <Text className="text-white" variant="compact-title">
            English
          </Text>
        </div>
        <div className="h-8 w-px rounded-sm bg-white" />
        <div className="flex flex-1 items-center justify-center">
          <LanguageSelectorDropdown
            selectedLanguage={selectedLanguage}
            onLanguageChange={onLanguageChange}
          />
          {translationModeEnabled && retranslate && (
            <Tooltip
              text={retranslateLoading ? "Retranslating..." : "Retranslate"}
              arrow
              placement="top"
              verticalOffset={7}
              horizontalOffset={28}
              style={{ display: "flex" }}
            >
              <motion.div
                className="ml-3"
                animate={retranslateLoading ? "loading" : undefined}
                variants={{
                  loading: {
                    rotate: -360,
                    transition: {
                      delay: 0,
                      duration: 1.2,
                      repeat: Infinity,
                      type: "spring",
                    },
                  },
                }}
              >
                <ArrowsRefreshIcon
                  className="size-4 cursor-pointer text-white"
                  onClick={() => retranslate(selectedLanguage)}
                />
              </motion.div>
            </Tooltip>
          )}
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-1/2 right-3 -translate-y-1/2 text-white hover:bg-white/20"
          >
            <XIcon className="size-4" />
          </Button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default TranslationBanner;
